<template>
    <div class="wrapper--slides" @wheel.passive="slideScroll">

        <page-main>
            <slot 
                name="wrapper"
                :attributes="attributes"
                :states="{
                    hidden,
                    playing,
                    sliding,
                }"
            />

            <SlideArrows 
                :moduleType="moduleType" 
                :visible="!playing" 
                v-show="!isMobile()" 
            />

            <Copyright 
                name="copyright" 
                :links="false" 
            />
        </page-main>

        <SlideProgress 
            :moduleType="moduleType" 
            v-if="!isMobile()" 
        />

        <SlideLines />

        <VideoFullscreen
            name="award-wining"
            media="Award_Wining_Culture"
        />

    </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import gsap from 'gsap'

import getDelta from '@/mixins/lib/getDelta'
import focusElement from '@/mixins/focusElement'
import initFullscreenVideo from '@/mixins/lib/initFullscreen'
import { createAlias } from '@/router/routesHelper'

import SlideLines       from './Slide/SlideLines'
import SlideProgress    from './Slide/SlideProgress'
import SlideArrows      from './Slide/SlideArrows'

const slideEvents = {
    beforeDestroy() {
        this.$root.$off('slide-goto')
    },
    mounted() {
        const vm = this 

        // Slides lines
        this.$root.$once('lines-end', () => {
            this.scrollIntoCurrentSlide()
            this.checkVisibility()

            vm.$root.$off('lines-end')
        })

        // Slides progress
        const slideProgressToStory = (i, storyId) => {
            return this.$store.dispatch(`${this.moduleType}/slideSetup`, { i, duration: getDelta(i, this.currentSlide) }).then(() => {
                if ( !isFinite(storyId) ) {
                    return;
                }
                if ( Array.isArray(storyId) ) {
                    const [ video ] = storyId
                    return this.$root.$emit(`fullscreen-award-${video}`, 'award-wining')
                }
                this.$root.$emit('show-stories', {id: storyId})
            })
        }

        const slideToStory = (i, storyId) => {
            if ( isFinite(storyId) ) {
                this.$root.$emit('slides-lock-scroll', true)
            }
            
            return slideProgressToStory.call(this, i, storyId)
        }

        // Slides navigation
        this.$root.$on('slide-goto', (i, storyId) => {
            // console.log(`slideSet ${i}  between`, vm.sliding)
            progressing.call(vm, () => vm.$root.$emit('slide-recruit-progress', { 
                fromNav: true,
                stage: 6,
                onComplete: slideToStory.bind(vm, i, storyId)
            }), 'blocked')
        })
    },
    methods: {
        scrollIntoCurrentSlide() {
            const { pathname: path } = window.location
            const alias = createAlias('discover', 'discover')
            const slideSectionId = alias.indexOf(path) + 1

            const currentSectionSelector = `.slides .slide--${slideSectionId}`
            const currentSection = document.querySelector(currentSectionSelector)
            
            // Ensure if section exists, but skip first one
            if ( slideSectionId === 1 || !currentSection ) return;

            const onComplete = () => 
                focusElement(`${currentSectionSelector} h2`, true)

            // ScrollTo current section
            return gsap.set(window, {
                scrollTo: {
                    y: currentSection, 
                    offsetY: 30,
                    onComplete
                },
            })
        },
    },
    watch: {
        mobileWatcher: {
            immediate: true,
            handler( isMobile ) {
                const action = isMobile ? 'rm' : 'add'
                this.scrollIntoCurrentSlide()
                return this.rootClass(action, 'no-overflow')
            }
        },
        wheeled( i ) {
            if ( this.isMobile() ) return;

            // Setup correct slide
            this.slideNav({ i }) 
        }
    },
}

export default {
    props: {
        moduleType: {
            type: String,
            default: 'discover'
        },
    },

    components: {
        SlideArrows,
        SlideLines,
        SlideProgress,
        VideoFullscreen: () => import(/* webpackChunkName: "ui-vid-fullscreen" */ './Ui/VideoFullscreen'),
    },
    
    data: () => ({
        visibleClass: '',
        activeElement: null,
        hidden      : false,
        playing     : false,
        sliding     : false,
        wheeled     : 0
    }),
    
    mixins: [
        slideEvents,
    ],

    computed: {
        mobileWatcher() {
            return this.$store.getters['browser/isMobile']
        },
        wrapperClass() {
            const { path } = this.$route

            if ( path.slice(1).startsWith('discover-') )
                return 'slides slides--discover'
            
            if ( path.slice(1).includes('-recruit-') )
                return 'slides slides--recruit'

                return ''
        },
        attributes() {
            const classes = this.visibleClass 
                ? `${this.wrapperClass} ${this.visibleClass}`
                : this.wrapperClass
            return {
                ref: 'slides',
                // role: 'group',
                class: classes,
                'data-flex': 'keep center',
                // 'aria-roledescription': 'carousel',
                // 'aria-label': 'Highlighted slides',
            }
        },
        ...mapState({
            slides(state, getters) {
                return getters[`${this.moduleType}/slides`]
            },
            currentSlide(state, getters) {
                return getters[`${this.moduleType}/currentSlide`]
            },
        })
    },

    deactivated() {
        this.$root.$off('fullscreen-award-play')
        this.$root.$off('slides-lock-scroll')
        
        this.$root.$off('show-stories')
        this.$root.$off('hide-stories')
    },

    activated() {
        const vm = this
        
        // Activate scrolling
        this.unlockSliding()

        // Setup correct slide
        window.onpopstate = ({ state }) => {
            if ( !vm.isMobile() ) {
                (state >= 0) && this.slideSet({ i: state })
            }
            this.checkVisibility()
        }
        
        // Handle Award_Winning video
        this.$root.$on('fullscreen-award-play', id => {

            this.hardLockSliding()
                
            initFullscreenVideo.call(vm, id, () => {
                vm.activeElement = document.activeElement
            }, () => {
                vm.unlockSliding()
                vm.activeElement && vm.activeElement.focus()
            })
        })

        this.$root.$on('slides-lock-scroll', (locked) => {
            if (locked) {
                this.hardLockSliding()
                return
            } 
            this.unlockSliding()
        })
    },

    provide() {
        return {
            moduleType: this.moduleType,
        }
    },

    methods: {
        slideScroll( w ) {
            if ( this.sliding || this.hidden ) {
                return;
            }
            progressing.call(this, () => {
                this.wheeled = Math.round(w.deltaY * w.timeStamp)
            }, 'sliding')
        },
        hardLockSliding() {
            this.hidden = this.sliding = this.playing = true
        },
        unlockSliding() {
            this.hidden = this.sliding = this.playing = false
        },
        checkVisibility() {
            const $slides = document.querySelector('.slides')
            if ( !$slides || this.visibleClass ) return;

            if ( !this.visibleClass ) requestAnimationFrame(() => {
                this.visibleClass = '-visible'
            })
        },
        ...mapMutations({
            slideNav(commit, payload) {
                return commit(`${this.moduleType}/slideNav`, payload)
            },
            slideSet(commit, payload) {
                return commit(`${this.moduleType}/slideSet`, payload)
            },
        })
    },
    
}

function progressing( callback, prop, timeout = 2000 ) {
    if ( !callback || this[prop] ) return;

    this[prop] = true

    if (callback instanceof Function) {
        callback()
    }

    setTimeout(() => {
        this[prop] = !this[prop]
    }, timeout)
}
</script>